//
// Copyright (c) 2021 Astute Global, Inc.
//

import i18next from "i18n/i18n";
import {
  addListToDropdown,
  Collection,
  createDropdown,
  createLabeledInputText,
  LabeledFieldView,
  LinkFormView,
  ViewModel,
} from "ckeditor5";
import ContentMappingSelect from "./contentMappingSelect";
import TopicLinkSelect from "./topicLinkSelect";
import { PLACEHOLDER_VALUE } from "../../ckEditorConstants";
import { removeCkReset } from "../../ckEditorUtil";
import {
  ATTRIBUTE_CONTENT_MAPPING,
  ATTRIBUTE_LINK_TOPIC,
  ATTRIBUTE_LINK_TYPE,
  buildTopicUrl,
  LINK_TYPE,
} from "../util";
import { createLabeledInputPhone } from "../../ui/inputPhoneView";
import { createLabeledInputEmail } from "../../ui/inputEmailView";

import "../css/akLinkFormView.css";

const LINK_TYPE_SELECTOR_INDEX = 1;

export default class AkLinkFormView extends LinkFormView {
  constructor(locale, validators) {
    super(locale, validators);
    this.set(ATTRIBUTE_CONTENT_MAPPING, undefined);
    this.set(ATTRIBUTE_LINK_TOPIC, undefined);
    this.set("kbId", undefined);
    this.set("languageId", undefined);
    this.set(ATTRIBUTE_LINK_TYPE, undefined);
    this.set("linkTypes", undefined);
    this.set("touchPointId", undefined);

    this.listenTo(this, "change:linkTypes", (_ev, _name, linkTypes) =>
      this._addLinkTypeSelectorView(linkTypes),
    );
    this.listenTo(this, `change:${ATTRIBUTE_LINK_TYPE}`, (_ev, _name, linkType) =>
      this._setupLinkType(linkType),
    );
  }

  get contentMappingView() {
    if (!this._contentMappingView) {
      this._contentMappingView = this._createContentMappingView();
    }
    return this._contentMappingView;
  }

  get emailAddressView() {
    if (!this._emailAddressView) {
      this._emailAddressView = this._createEmailAddressView();
    }
    return this._emailAddressView;
  }

  get emailBodyView() {
    if (!this._emailBodyView) {
      this._emailBodyView = this._createEmailBodyView();
    }
    return this._emailBodyView;
  }

  get emailSubjectView() {
    if (!this._emailSubjectView) {
      this._emailSubjectView = this._createEmailSubjectView();
    }
    return this._emailSubjectView;
  }

  isValid() {
    for (const child of this.children) {
      if (child.isValid && !child.isValid()) {
        return false;
      }
      if (child.fieldView && child.fieldView.isValid && !child.fieldView.isValid()) {
        return false;
      }
    }
    return true;
  }

  get phoneNumberView() {
    if (!this._phoneNumberView) {
      this._phoneNumberView = this._createPhoneNumberView();
    }
    return this._phoneNumberView;
  }

  get topicLinkView() {
    if (!this._topicLinkView) {
      this._topicLinkView = this._createTopicLinkView();
    }
    return this._topicLinkView;
  }

  get url() {
    return this.urlInputView?.fieldView?.element?.value;
  }

  set url(url) {
    if (this.urlInputView) {
      this.urlInputView.fieldView.value = url;
    }
  }

  render() {
    super.render();
    this._addViewsToFocusables(
      this.linkTypeSelectorView,
      this._contentMappingView,
      this._emailAddressView,
      this._emailSubjectView,
      this._emailBodyView,
      this._phoneNumberView,
      this._topicLinkView,
    );
    // Remove the CKEditor reset classes to avoid problems integrating with Marionette
    setTimeout(() => removeCkReset(this.element), 100);
  }

  _addChildViews(...views) {
    if (!this.children.has(views[0])) {
      if (this.providersListChildren.length === 0) {
        this.children.addMany(views);
      } else {
        this.children.addMany(views, this.children.length - 1);
      }
      for (const view of views) {
        if (view.isRendered) {
          this._addViewsToFocusables(view);
        } else {
          view.once("render", () => this._addViewsToFocusables(view));
        }
      }
      return true;
    }
    return false;
  }

  _addLinkTypeSelectorView(linkTypes) {
    if (this.linkTypeSelectorView) {
      this.children.remove(this.linkTypeSelectorView);
    }
    this.linkTypeSelectorView = this._createLinkTypeSelectorView(linkTypes);
    this.children.add(this.linkTypeSelectorView, LINK_TYPE_SELECTOR_INDEX);
  }

  _addContentMappingView() {
    this._addChildViews(this.contentMappingView);
  }

  _addEmailViews() {
    const emailAddressView = this.emailAddressView;
    const emailSubjectView = this.emailSubjectView;
    const emailBodyView = this.emailBodyView;
    if (this._addChildViews(emailAddressView, emailSubjectView, emailBodyView)) {
      const emailParts = this._parseMailtoUrl(this.url);
      if (emailParts) {
        emailAddressView.fieldView.value = emailParts.address;
        emailSubjectView.fieldView.value = emailParts.subject;
        emailBodyView.fieldView.value = emailParts.body;
      }
      const syncMailtoUrl = () => {
        const newUrl = this._buildMailtoUrl(emailAddressView, emailSubjectView, emailBodyView);
        this.url = newUrl;
        if (newUrl) {
          emailAddressView.errorText = "";
        } else {
          emailAddressView.errorText = i18next.t("commonMessages.emptyMessage");
        }
      };
      this.listenTo(emailAddressView.fieldView, "input", syncMailtoUrl);
      this.listenTo(emailSubjectView.fieldView, "input", syncMailtoUrl);
      this.listenTo(emailBodyView.fieldView, "input", syncMailtoUrl);
    }
  }

  _addPhoneNumberView() {
    const phoneNumberView = this.phoneNumberView;
    if (this._addChildViews(phoneNumberView)) {
      let currentValue = this.url;
      if (currentValue && currentValue.startsWith("tel:")) {
        phoneNumberView.fieldView.value = currentValue.substring(4);
      }
      this.listenTo(phoneNumberView.fieldView, "input", (_evt, inputEvent) => {
        this.url = "tel:" + inputEvent.currentTarget.value;
      });
    }
  }

  _addTopicLinkView() {
    this._addChildViews(this.topicLinkView);
  }

  _addViewsToFocusables(...views) {
    if (views && views.length > 0 && this.isRendered) {
      for (const view of views) {
        if (view) {
          if (!this._focusables.has(view)) {
            // Register the view as focusable.
            this._focusables.add(view);
          }
          if (!this.focusTracker.elements.includes(view.element)) {
            // Register the view in the focus tracker.
            this.focusTracker.add(view.element);
          }
        }
      }
    }
  }

  _buildMailtoUrl(emailAddressView, emailSubjectView, emailBodyView) {
    const emailAddress = emailAddressView.fieldView.element.value;
    if (!emailAddress || emailAddress == PLACEHOLDER_VALUE) {
      return "";
    }
    const emailSubject = emailSubjectView.fieldView.element.value;
    const emailBody = emailBodyView.fieldView.element.value;
    let url = "mailto:" + emailAddress;
    let separator = "?";
    if (emailSubject || emailBody) {
      if (emailSubject) {
        url += separator + "subject=" + encodeURIComponent(emailSubject.trim());
        separator = "&";
      }
      if (emailBody) {
        url += separator + "body=" + encodeURIComponent(emailBody.trim());
      }
    }
    return url;
  }

  _buildTopicUrl(model) {
    return buildTopicUrl(this.kbId, model.id);
  }

  _createContentMappingView() {
    return new LabeledFieldView(this.locale, (labeledFieldView, viewUid, statusUid) => {
      const contentMappingSelect = new ContentMappingSelect(labeledFieldView.locale);
      contentMappingSelect.set({
        id: viewUid,
        ariaDescribedById: statusUid,
        languageId: this.languageId,
        touchPointId: this.touchPointId,
      });
      labeledFieldView.bind("errorText").to(contentMappingSelect);
      contentMappingSelect.bind("languageId", "touchPointId").to(this);
      contentMappingSelect
        .bind("contentMappingId")
        .to(this, ATTRIBUTE_CONTENT_MAPPING, (contentMappingJson) =>
          contentMappingJson && contentMappingJson != PLACEHOLDER_VALUE
            ? JSON.parse(contentMappingJson).id
            : undefined,
        );
      this.listenTo(contentMappingSelect, "contentMapping:select", (_evt, _model, href) => {
        this.urlInputView.fieldView.value = href;
      });
      return contentMappingSelect;
    });
  }

  _createEmailAddressView() {
    const emailAddressView = new LabeledFieldView(this.locale, createLabeledInputEmail);
    emailAddressView.label = i18next.t("ckeditor.email.address");
    return emailAddressView;
  }

  _createEmailBodyView() {
    const emailBodyView = new LabeledFieldView(this.locale, createLabeledInputText);
    emailBodyView.label = i18next.t("ckeditor.email.body");
    return emailBodyView;
  }

  _createEmailSubjectView() {
    const emailSubjectView = new LabeledFieldView(this.locale, createLabeledInputText);
    emailSubjectView.label = i18next.t("ckeditor.email.subject");
    return emailSubjectView;
  }

  _createLinkSelectorOption(linkType) {
    const linkTypeModel = new ViewModel({
      label: i18next.t("ckeditor.linkType." + linkType),
      linkType: linkType,
      role: "menuitemradio",
      withText: true,
    });
    linkTypeModel.bind("isOn").to(this, ATTRIBUTE_LINK_TYPE, (lt) => lt == linkType);
    return {
      type: "button",
      model: linkTypeModel,
    };
  }

  _createLinkTypeSelectorView(linkTypes) {
    const linkTypeLabel = i18next.t("ckeditor.linkType.label");
    const linkDropdown = createDropdown(this.locale);
    const linkOptions = new Collection();
    for (const linkType of Object.values(linkTypes)) {
      linkOptions.add(this._createLinkSelectorOption(linkType));
    }
    addListToDropdown(linkDropdown, linkOptions, {
      ariaLabel: linkTypeLabel,
      role: "menu",
    });
    linkDropdown.buttonView.set({
      ariaLabel: linkTypeLabel,
      ariaLabelledBy: undefined,
      isOn: false,
      withText: true,
      tooltip: linkTypeLabel,
    });
    linkDropdown.buttonView.extendTemplate({
      attributes: {
        class: "ck-dropdown__button_label-width_auto",
      },
    });
    linkDropdown.extendTemplate({
      attributes: {
        class: "ak-link-type-dropdown",
      },
    });
    linkDropdown.buttonView
      .bind("label")
      .to(this, ATTRIBUTE_LINK_TYPE, (lt) => i18next.t("ckeditor.linkType." + lt));
    this.listenTo(linkDropdown, "execute", (ev) => {
      const { linkType } = ev.source;
      this.set(ATTRIBUTE_LINK_TYPE, linkType);
    });
    return linkDropdown;
  }

  _createPhoneNumberView() {
    const phoneNumberView = new LabeledFieldView(this.locale, createLabeledInputPhone);
    phoneNumberView.label = i18next.t("ckeditor.phone.number");
    return phoneNumberView;
  }

  _createTopicLinkView() {
    return new LabeledFieldView(this.locale, (labeledFieldView, viewUid, statusUid) => {
      const topicLinkSelect = new TopicLinkSelect(this.locale, this.languageId);
      topicLinkSelect.set({
        id: viewUid,
        ariaDescribedById: statusUid,
        languageId: this.languageId,
      });
      labeledFieldView.bind("errorText").to(topicLinkSelect);
      topicLinkSelect.bind("languageId").to(this);
      topicLinkSelect
        .bind("topicId")
        .to(this, ATTRIBUTE_LINK_TOPIC, (topic) =>
          topic && topic != PLACEHOLDER_VALUE ? JSON.parse(topic).id : undefined,
        );
      this.listenTo(topicLinkSelect, "topicLink:select", (_evt, model) => {
        this.urlInputView.fieldView.value = this._buildTopicUrl(model);
        this.set(
          ATTRIBUTE_LINK_TOPIC,
          JSON.stringify({
            id: topicLinkSelect.topicId,
            label: topicLinkSelect.topicLabel,
          }),
        );
      });
      return topicLinkSelect;
    });
  }

  _parseMailtoUrl(url) {
    if (!url || !url.startsWith("mailto:")) {
      return null;
    }
    const emailParts = {
      address: "",
      body: "",
      subject: "",
    };
    let remaining = url.substring(7).trim();
    let questionPos = remaining.indexOf("?");
    if (questionPos < 0) {
      emailParts.address = remaining;
      return emailParts;
    }
    emailParts.address = remaining.substring(0, questionPos).trim();
    remaining = remaining.substring(questionPos + 1).trim();
    if (!remaining) {
      return emailParts;
    }
    const paramParts = remaining.split("&");
    for (const paramPart of paramParts) {
      const equalPos = paramPart.indexOf("=");
      if (equalPos <= 0) {
        continue;
      }
      const key = decodeURIComponent(paramPart.substring(0, equalPos).trim());
      const value = decodeURIComponent(paramPart.substring(equalPos + 1).trim());
      switch (key) {
        case "body":
          emailParts.body = value;
          break;
        case "subject":
          emailParts.subject = value;
          break;
      }
    }
    return emailParts;
  }

  _removeContentMappingView() {
    if (this._contentMappingView && this.children.has(this._contentMappingView)) {
      this.children.remove(this._contentMappingView);
    }
  }

  _removeEmailViews() {
    if (this._emailAddressView && this.children.has(this._emailAddressView)) {
      this.children.remove(this._emailAddressView);
      this.children.remove(this._emailSubjectView);
      this.children.remove(this._emailBodyView);
      this.stopListening(this._emailAddressView, "input");
      this.stopListening(this._emailSubjectView, "input");
      this.stopListening(this._emailBodyView, "input");
      this._emailAddressView.fieldView.value = "";
      this._emailSubjectView.fieldView.value = "";
      this._emailBodyView.fieldView.value = "";
    }
  }

  _removePhoneNumberView() {
    if (this._phoneNumberView && this.children.has(this._phoneNumberView)) {
      this.children.remove(this._phoneNumberView);
      this.stopListening(this._phoneNumberView, "input");
      this._phoneNumberView.fieldView.value = "";
    }
  }

  _removeTopicLinkView() {
    if (this._topicLinkView && this.children.has(this._topicLinkView)) {
      this.children.remove(this._topicLinkView);
    }
  }

  _setupLinkType(linkType) {
    switch (linkType) {
      case LINK_TYPE.CONTENT_MAPPING:
        this._removeEmailViews();
        this._removePhoneNumberView();
        this._removeTopicLinkView();
        this._addContentMappingView();
        break;
      case LINK_TYPE.EMAIL:
        this._removeContentMappingView();
        this._removePhoneNumberView();
        this._removeTopicLinkView();
        this._addEmailViews();
        break;
      case LINK_TYPE.PHONE:
        this._removeContentMappingView();
        this._removeEmailViews();
        this._removeTopicLinkView();
        this._addPhoneNumberView();
        break;
      case LINK_TYPE.TOPIC_LINK:
        this._removeContentMappingView();
        this._removeEmailViews();
        this._removePhoneNumberView();
        this._addTopicLinkView();
        this._turnOffNewTab();
        break;
      default:
        this._removeContentMappingView();
        this._removeEmailViews();
        this._removePhoneNumberView();
        this._removeTopicLinkView();
        break;
    }
  }

  _turnOffNewTab() {
    if (this.newTabDecorator) {
      this.newTabDecorator.value = false;
    }
  }
}
